import React, { createContext, useCallback, useContext, useState } from 'react';
import {
    clearGoogleIdToken,
    decodeJwtPayload,
    exchangeGoogleIdTokenForEasyAuthToken,
    getGoogleIdToken,
    setEasyAuthToken,
    setGoogleIdToken as storeToken,
} from '../googleAuth';

interface GoogleAuthContextValue {
    isGoogleAuthenticated: boolean;
    googleUserName: string | null;
    signInWithGoogle: (idToken: string) => Promise<void>;
    signOutGoogle: () => void;
}

const GoogleAuthContext = createContext<GoogleAuthContextValue>({
    isGoogleAuthenticated: false,
    googleUserName: null,
    signInWithGoogle: async () => {},
    signOutGoogle: () => {},
});

export const GoogleAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(() => !!getGoogleIdToken());
    const [googleUserName, setGoogleUserName] = useState<string | null>(
        () => sessionStorage.getItem('google_user_name')
    );

    const signInWithGoogle = useCallback(async (idToken: string) => {
        const authToken = await exchangeGoogleIdTokenForEasyAuthToken(idToken);
        setEasyAuthToken(authToken);
        storeToken(idToken);
        const payload = decodeJwtPayload(idToken);
        const name = (payload['name'] as string) ?? (payload['email'] as string) ?? null;
        if (name) {
            sessionStorage.setItem('google_user_name', name);
            setGoogleUserName(name);
        }
        setIsGoogleAuthenticated(true);
    }, []);

    const signOutGoogle = useCallback(() => {
        clearGoogleIdToken();
        setGoogleUserName(null);
        setIsGoogleAuthenticated(false);
    }, []);

    return (
        <GoogleAuthContext.Provider
            value={{ isGoogleAuthenticated, googleUserName, signInWithGoogle, signOutGoogle }}
        >
            {children}
        </GoogleAuthContext.Provider>
    );
};

export function useGoogleAuth(): GoogleAuthContextValue {
    return useContext(GoogleAuthContext);
}
